import { useState, useRef } from "react";

import api from "@helpers/api";
import validate from "@helpers/validate";

import * as Mui from "@mui/material";

import CustomModal from "@components/CustomModal";

import "./styles.css";

import { WalletRounded, HomeRounded, PersonRounded } from "@mui/icons-material";

import IncomingBankAccount from "./components/IncomingBankAccount";
import BankAddress from "./components/BankAddress";
import BankDocument from "./components/BankDocument";

const steps = [
    { label: "Conta de recebimentos", icon: WalletRounded, root: "withdrawal" },
    { label: "Endereço bancário", icon: HomeRounded, root: "address" },
    { label: "Documentos", icon: PersonRounded, root: "owner" },
];

// Helper functions for validation


export default function CreateBankAccountModal({ modalRef }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [skipped, setSkipped] = useState(new Set());

    const [errors, setErrors] = useState()

    let REF_callback = useRef(() => { });

    const REF_values = useRef({
        "withdrawal": {
            "type": "pix",
        },
        "address": {
            "street": "",
            "number": "",
            "complement": "",
            "neighborhood": "",
            "city": "",
            "state": "",
            "zipcode": "",
        },
        "owner": {
            "name": "",
            "birthdate": "",
            "monthly_income": "",
            "document": {
                "number": "",
                "identification": "cpf",
                "type": "individual",
            },
            "company_type": "LIMITED",
            "type": "INDIVIDUAL",
            "phone": {
                "number": "",
            }
        }
    });

    if (!modalRef) return null;

    modalRef.current = {
        open(callback = () => { }) {
            REF_callback.current = callback;
            setOpen(true);
            setActiveStep(0);
            setSkipped(new Set());
        },
    };

    const hasError = {
        withdrawal: () => {
            const withdrawal = REF_values.current.withdrawal;
            if (!withdrawal.key) return "A chave pix é obrigatória.";
            return false;
        },
        address: () => {
            const address = REF_values.current.address;
            if (!address.zipcode) return "O CEP é obrigatório.";
            if (!address.street) return "O nome da rua é obrigatório.";
            if (!address.number) return "O número do endereço é obrigatório.";
            if (!address.neighborhood) return "O bairro é obrigatório.";
            if (!address.city) return "A cidade é obrigatória.";
            if (!address.state) return "O estado é obrigatório.";
            return false;
        },
        owner: () => {
            const owner = REF_values.current.owner;
            if ((!owner.birthdate || !validate.date(owner.birthdate)) && owner.type === 'INDIVIDUAL') return "A data de nascimento é obrigatória e deve ser válida.";
            if (!owner.monthly_income) return "A renda mensal é obrigatória.";
            if ((!owner.document.number || !validate.cnpj(owner.document.number)) && owner.type === 'COMPANY') return "O número do documento (CNPJ) é obrigatório e deve ser válido.";
            if ((!owner.document.number || !validate.cpf(owner.document.number)) && owner.type === 'INDIVIDUAL') return "O número do documento (CPF) é obrigatório e deve ser válido.";
            if (!owner.phone.number || !validate.phone.br(owner.phone.number)) return "O número de telefone é obrigatório e deve ser válido.";
            return false;
        }
    }

    const isStepSkipped = (step) => skipped.has(step);

    const handleNext = () => {
        try {
            const error = hasError[steps[activeStep].root]()

            if (error) {
                setErrors(error)
                return false
            }

            setErrors(null)

        } catch (e) {
            console.log('Next error', e)
            return false
        }

        let newSkipped = skipped;
        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values());
            newSkipped.delete(activeStep);
        }
        if (activeStep < steps.length - 1) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
            setSkipped(newSkipped);
        } else {
            handleCreateBankAccount();
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    async function handleCreateBankAccount() {
        setLoading(true);
        try {
            const payload = getPayload()

            const { data } = await api.post("wallet", payload);
            if (!data.id) {
                setErrors('Houve um erro ao criar a conta, verifique os dados e tente novamente.')
            } else {
                window.setSnackbar("Conta de pagamentos criada com sucesso")
                REF_callback.current();
                setOpen(false)
            }
        } catch (e) {
            setErrors('Um erro ocorreu ao criar a conta, verifique os dados e tente novamente.')
            console.log(e);
        }
        setLoading(false);

        function getPayload() {
            return REF_values.current
        }
    }

    function getStepContent(step) {
        switch (step) {
            case 0:
                return <IncomingBankAccount REF_values={REF_values} />;
            case 1:
                return <BankAddress REF_values={REF_values} />;
            case 2:
                return <BankDocument REF_values={REF_values} />;
            default:
                return null;
        }
    }

    const submitText = activeStep === steps.length - 1 ? "Salvar conta" : "Próximo";
    const backText = activeStep === 0 ? "Cancelar" : "Voltar";

    return (
        <CustomModal
            maxWidth={510}
            handleSubmit={handleNext}
            bottomButtonSumbitText={submitText}
            bottomButtonCancelText={backText}
            handleSecondary={activeStep === 0 ? () => setOpen(false) : handleBack}
            loading={loading}
            title="Criação de conta de pagamentos"
            open={open}
            handleClose={() => setOpen(false)}
            bodyComponent={
                open ? (
                    <div className="px-4 py-2 user-details">
                        <CurrentStepIndicator
                            steps={steps}
                            activeStep={activeStep}
                            skipped={skipped}
                        />
                        {activeStep !== steps.length && (
                            <>
                                <div style={{ minHeight: 240, marginTop: 24 }}>
                                    {getStepContent(activeStep)}
                                </div>
                            </>
                        )}
                        {
                            errors && (
                                <Mui.Alert severity="error">
                                    {errors}
                                </Mui.Alert>
                            )
                        }
                    </div>
                ) : null
            }
        />
    );
}

function CurrentStepIndicator({ steps, activeStep, skipped }) {
    function CustomStepIcon(props) {
        const { active, completed, icon } = props;
        const stepIdx = Number(icon) - 1;
        const IconComponent = steps[stepIdx]?.icon;
        let bg = 'var(--primary-1)';
        let color = 'var(--primary-6)';
        let opacity = 1;
        if (active) {
            bg = 'var(--primary-6)';
            color = '#fff';
        } else if (stepIdx > activeStep) {
            opacity = 0.9;
        } else if (completed) {
            bg = 'var(--primary-1)';
            color = 'var(--primary-6)';
        }
        return (
            <span
                className="step-icon"
                style={{
                    borderColor: stepIdx !== activeStep ? 'var(--primary-2) !important' : 'var(--primary-3) !important',
                    background: bg,
                    color,
                    opacity,
                }}
            >
                {IconComponent ? <IconComponent style={{ color, fontSize: 20 }} /> : icon}
            </span>
        );
    }

    const isStepSkipped = (step) => skipped.has(step);

    return (
        <Mui.Stepper activeStep={activeStep} alternativeLabel connector={<Mui.StepConnector />} className="mb-5 mt-2 account-stepper">
            {steps.map((step, index) => {
                const stepProps = {};
                const labelProps = { StepIconComponent: CustomStepIcon };
                if (isStepSkipped(index)) {
                    stepProps.completed = false;
                }
                return (
                    <Mui.Step key={step.label} {...stepProps}>
                        <Mui.StepLabel {...labelProps}>{step.label}</Mui.StepLabel>
                    </Mui.Step>
                );
            })}
        </Mui.Stepper>
    );
}
